import { inject, Injectable } from '@angular/core';
import type { OAuth2RefreshTokenOptions } from '@capacitor-community/generic-oauth2';
import { GenericOAuth2 } from '@capacitor-community/generic-oauth2';
import { App } from '@capacitor/app';
import { CustomerIdentityTenantConfig } from '@ld.consumer-portal/models';
import type { CiamCallbackResult } from './ciam-auth.service';
import { EntraLoginAccelerationEvaluatorService } from './entra-login-acceleration-evaluator.service';
import { LoggingService } from './logging.service';

interface NativeAuthOptions {
  appId: string;
  /** Fully qualified CIAM login host, e.g. `ldcorpciamdv.ciamlogin.com`. Doubles as the Entra `domain_hint`. */
  ciamDomain: string;
  entraTenantID: string;
  email?: string;
}

/**
 * Native CIAM authentication service using @capacitor-community/generic-oauth2.
 *
 * Used on iOS/Android to handle Entra CIAM login via system browser
 * (ASWebAuthenticationSession on iOS, Chrome Custom Tabs on Android)
 * with Authorization Code + PKCE flow.
 *
 * Web continues to use CiamAuthService with @azure/msal-browser redirects.
 */
@Injectable({ providedIn: 'root' })
export class NativeCiamAuthService {
  private readonly loggingService = inject(LoggingService);
  private readonly entraLoginAccelerationService = inject(EntraLoginAccelerationEvaluatorService);

  private lastAuthOptions: NativeAuthOptions | null = null;

  async login(
    tenantConfig: CustomerIdentityTenantConfig,
    email: string,
    mobileAppId?: string,
  ): Promise<CiamCallbackResult> {
    const ciamDomain = tenantConfig.ciamDomain;
    if (!ciamDomain || ciamDomain.trim() === '') {
      throw new Error(
        'CIAM domain is not available from TenantConfig. Ensure ciamDomain is returned by the TenantConfiguration API.',
      );
    }

    const fullCiamDomain = `${ciamDomain}.ciamlogin.com`;

    // Use mobile-specific app ID from environment config, or fall back to tenant config's applicationId
    const appId = mobileAppId || tenantConfig.applicationId;

    const persistedAuthOptions: NativeAuthOptions = {
      appId,
      ciamDomain: fullCiamDomain,
      entraTenantID: tenantConfig.entraTenantID,
    };
    // The email stays in memory only: this blob survives sign-out so biometric refresh can
    // still resolve the tenant endpoints, and a shared device must not retain the last user.
    this.lastAuthOptions = { ...persistedAuthOptions, email };
    localStorage.setItem('native_ciam_auth_options', JSON.stringify(persistedAuthOptions));

    // Last step before authorize: blocklisted domains resolve to their own Entra home tenant
    // when a login_hint is sent, which drops the user on that tenant's password step. Resolving
    // here also holds the email re-entry notice on screen until the system browser opens.
    // See EntraLoginAccelerationEvaluatorService.
    const loginHint = await this.entraLoginAccelerationService.resolveLoginHint(email);

    this.loggingService.logInfo(
      `[NativeCiamAuth] login() starting | email=${email} | appId=${appId} | domain=${fullCiamDomain} | tenant=${tenantConfig.entraTenantID} | sendLoginHint=${!!loginHint}`,
    );

    let result: Record<string, unknown>;
    try {
      result = await this.authorize(this.lastAuthOptions, {
        prompt: 'login',
        ...(loginHint ? { login_hint: loginHint } : {}),
      });
    } finally {
      // The app keeps running behind the system browser, so the notice has to come down once
      // that browser closes — whether the borrower signed in, failed or cancelled.
      this.entraLoginAccelerationService.hideEmailReentryNotice();
    }

    const topLevelKeys = Object.keys(result);
    const atr = result['access_token_response'] as Record<string, unknown> | undefined;
    const atrKeys = atr ? Object.keys(atr) : [];
    this.loggingService.logInfo(
      `[NativeCiamAuth] login() OAuth2 authenticate completed | topLevelKeys=[${topLevelKeys.join(',')}] | access_token_response keys=[${atrKeys.join(',')}] | hasRefreshTokenInATR=${!!atr?.['refresh_token']} | hasRefreshTokenInRoot=${!!result['refresh_token']}`,
    );
    return this.mapAuthResult(result, email);
  }

  /**
   * Runs an Entra CIAM step-up MFA challenge in the system browser.
   *
   * MSAL's popup/redirect step-up (CiamAuthService.requestStepUpMfa) cannot run inside
   * Capacitor, so native re-runs the Authorization Code + PKCE flow with the `claims`
   * request carrying the required authentication context (`acrs`).
   *
   * `prompt=login` is sent for the same reason as `login()`: the system browser runs an
   * ephemeral session, so without it Entra attempts a silent sign-in that always fails and
   * renders "Something went wrong and we can't sign you in right now" above the credential
   * form before falling back to it.
   */
  async stepUp(authContextId: string, email?: string): Promise<CiamCallbackResult> {
    const authOptions = this.lastAuthOptions ?? this.restoreAuthOptions();
    if (!authOptions) {
      throw new Error('Native CIAM auth options are not available. User must log in before requesting step-up MFA.');
    }
    this.lastAuthOptions = authOptions;

    const claims = JSON.stringify({
      access_token: {
        acrs: {
          essential: true,
          value: authContextId,
        },
      },
    });

    const loginHint = email ?? authOptions.email;
    const additionalParameters: Record<string, string> = {
      claims,
      prompt: 'login',
    };
    if (loginHint) {
      additionalParameters['login_hint'] = loginHint;
    }

    this.loggingService.logInfo(
      `[NativeCiamAuth] stepUp() starting | authContextId=${authContextId} | appId=${authOptions.appId} | domain=${authOptions.ciamDomain} | tenant=${authOptions.entraTenantID} | hasLoginHint=${!!loginHint}`,
    );

    const result = await this.authorize(authOptions, additionalParameters);

    this.loggingService.logInfo(
      `[NativeCiamAuth] stepUp() OAuth2 authenticate completed | topLevelKeys=[${Object.keys(result).join(',')}]`,
    );

    return this.mapAuthResult(result, loginHint ?? '');
  }

  async refreshToken(): Promise<CiamCallbackResult | null> {
    if (!this.lastAuthOptions) {
      this.lastAuthOptions = this.restoreAuthOptions();
    }
    if (!this.lastAuthOptions) {
      this.loggingService.logWarning(
        `[NativeCiamAuth] refreshToken() ABORTED: no auth options | inMemory=false | localStorage=${!!localStorage.getItem('native_ciam_auth_options')}`,
      );
      return null;
    }
    this.loggingService.logInfo(
      `[NativeCiamAuth] refreshToken() auth options restored | appId=${this.lastAuthOptions.appId} | domain=${this.lastAuthOptions.ciamDomain}`,
    );

    const storedRefreshToken = localStorage.getItem('native_ciam_refresh_token');
    if (!storedRefreshToken) {
      this.loggingService.logWarning('[NativeCiamAuth] refreshToken() no stored refresh token found in localStorage');
      return null;
    }

    this.loggingService.logInfo(
      `[NativeCiamAuth] refreshToken() attempting silent refresh | tokenLength=${storedRefreshToken.length} | endpoint=${this.lastAuthOptions.ciamDomain}/${this.lastAuthOptions.entraTenantID}`,
    );

    try {
      const options: OAuth2RefreshTokenOptions = {
        appId: this.lastAuthOptions.appId,
        accessTokenEndpoint: `https://${this.lastAuthOptions.ciamDomain}/${this.lastAuthOptions.entraTenantID}/oauth2/v2.0/token`,
        refreshToken: storedRefreshToken,
        scope: 'openid profile offline_access api://consumerportal/user_impersonation',
      };

      const result = await GenericOAuth2.refreshToken(options);

      if (result['refresh_token']) {
        const newToken = result['refresh_token'] as string;
        localStorage.setItem('native_ciam_refresh_token', newToken);
        this.loggingService.logInfo(
          `[NativeCiamAuth] refreshToken() new refresh token received | length=${newToken.length}`,
        );
      } else {
        this.loggingService.logWarning('[NativeCiamAuth] refreshToken() no new refresh token in response (using existing)');
      }

      this.loggingService.logInfo('[NativeCiamAuth] refreshToken() silent refresh succeeded');
      return this.mapAuthResult(result, '');
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : String(error);
      const errStack = error instanceof Error ? error.stack?.split('\n').slice(0, 3).join(' | ') : '';
      this.loggingService.logWarning(
        `[NativeCiamAuth] refreshToken() silent refresh FAILED | error=${errMsg} | stack=${errStack} | endpoint=${this.lastAuthOptions.ciamDomain}/${this.lastAuthOptions.entraTenantID}`,
      );
      localStorage.removeItem('native_ciam_refresh_token');
      return null;
    }
  }

  async logout(): Promise<void> {
    localStorage.removeItem('native_ciam_refresh_token');
    // Keep native_ciam_auth_options so refreshToken() can find the token endpoint
    // after biometric login restores the refresh token from the native keychain.
  }

  private async authorize(
    authOptions: NativeAuthOptions,
    additionalParameters: Record<string, string>,
  ): Promise<Record<string, unknown>> {
    const bundleId = await this.getBundleId();
    const iosRedirectUrl = `msauth.${bundleId}://auth`;
    const androidRedirectUrl = `${bundleId}://oauth`;
    const accessTokenEndpoint = `https://${authOptions.ciamDomain}/${authOptions.entraTenantID}/oauth2/v2.0/token`;
    const appId = authOptions.appId;

    return GenericOAuth2.authenticate({
      appId,
      authorizationBaseUrl: `https://${authOptions.ciamDomain}/${authOptions.entraTenantID}/oauth2/v2.0/authorize`,
      accessTokenEndpoint,
      scope: 'openid profile offline_access api://consumerportal/user_impersonation',
      responseType: 'code',
      pkceEnabled: true,
      logsEnabled: false,
      additionalParameters,
      android: {
        appId,
        responseType: 'code',
        pkceEnabled: true,
        redirectUrl: androidRedirectUrl,
        accessTokenEndpoint,
      },
      ios: {
        appId,
        responseType: 'code',
        pkceEnabled: true,
        redirectUrl: iosRedirectUrl,
        accessTokenEndpoint,
      },
    });
  }

  private mapAuthResult(result: Record<string, unknown>, email: string): CiamCallbackResult {
    // The generic-oauth2 plugin returns tokens nested in access_token_response
    const tokenResponse = (result['access_token_response'] as Record<string, unknown>) ?? result;

    const accessToken = (tokenResponse['access_token'] as string) ?? '';
    const idToken = (tokenResponse['id_token'] as string) ?? '';

    if (tokenResponse['refresh_token']) {
      localStorage.setItem('native_ciam_refresh_token', tokenResponse['refresh_token'] as string);
      this.loggingService.logInfo(
        `[NativeCiamAuth] mapAuthResult: refresh_token FOUND and stored | length=${(tokenResponse['refresh_token'] as string).length}`,
      );
    } else {
      this.loggingService.logWarning(
        `[NativeCiamAuth] mapAuthResult: NO refresh_token in token response | tokenResponseKeys=[${Object.keys(tokenResponse).join(',')}]`,
      );
    }

    let expiresOn: Date | null = null;
    if (tokenResponse['expires_on']) {
      expiresOn = new Date(tokenResponse['expires_on'] as string);
    } else if (tokenResponse['expires_in']) {
      expiresOn = new Date(Date.now() + (tokenResponse['expires_in'] as number) * 1000);
    }

    const tokenPayload = this.decodeJwtPayload(idToken);

    // CIAM tokens: use 'oid' (object ID / GUID) for user identification
    // The 'sub' claim in CIAM is a base64 string, not a GUID like legacy B2C
    const oid = (tokenPayload?.['oid'] as string) ?? '';

    const mappedResult = {
      accessToken,
      idToken,
      expiresOn,
      account: {
        homeAccountId: oid,
        localAccountId: oid,
        name: (tokenPayload?.['name'] as string) ?? undefined,
        username: email || ((tokenPayload?.['preferred_username'] as string) ?? ''),
      },
    };

    this.logTokenMetadata('id_token', tokenPayload);
    this.logTokenMetadata('access_token', this.decodeJwtPayload(accessToken));

    const storedRefresh = localStorage.getItem('native_ciam_refresh_token');
    this.loggingService.logInfo(
      `[NativeCiamAuth] mapAuthResult() summary | hasAccessToken=${!!accessToken} | hasIdToken=${!!idToken} | hasRefreshToken=${!!storedRefresh} | expiresOn=${expiresOn?.toISOString() ?? 'N/A'} | oid=${oid} | email=${mappedResult.account.username}`,
    );

    return mappedResult;
  }

  private logTokenMetadata(tokenType: string, payload: Record<string, unknown> | null): void {
    if (!payload) {
      this.loggingService.logWarning(`[NativeCiamAuth] ${tokenType} could not be decoded (not a JWT or empty)`);
      return;
    }

    const exp = payload['exp'] as number | undefined;
    const iat = payload['iat'] as number | undefined;
    const nbf = payload['nbf'] as number | undefined;
    const aud = payload['aud'] as string | undefined;
    const iss = payload['iss'] as string | undefined;
    const sub = payload['sub'] as string | undefined;
    const oid = payload['oid'] as string | undefined;
    const scp = payload['scp'] as string | undefined;

    const now = Math.floor(Date.now() / 1000);
    const expiresIn = exp ? exp - now : undefined;
    const lifetimeMinutes = exp && iat ? Math.round((exp - iat) / 60) : undefined;

    this.loggingService.logInfo(
      `[NativeCiamAuth] ${tokenType} metadata:` +
        `\n  iss=${iss ?? 'N/A'}` +
        `\n  aud=${aud ?? 'N/A'}` +
        `\n  sub=${sub ? sub.substring(0, 12) + '...' : 'N/A'}` +
        `\n  oid=${oid ?? 'N/A'}` +
        `\n  scp=${scp ?? 'N/A'}` +
        `\n  iat=${iat ? new Date(iat * 1000).toISOString() : 'N/A'}` +
        `\n  nbf=${nbf ? new Date(nbf * 1000).toISOString() : 'N/A'}` +
        `\n  exp=${exp ? new Date(exp * 1000).toISOString() : 'N/A'}` +
        `\n  expiresIn=${expiresIn !== undefined ? `${expiresIn}s (${Math.round(expiresIn / 60)}min)` : 'N/A'}` +
        `\n  tokenLifetime=${lifetimeMinutes !== undefined ? `${lifetimeMinutes}min` : 'N/A'}`,
    );
  }

  private decodeJwtPayload(token: string): Record<string, unknown> | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  private restoreAuthOptions(): NativeAuthOptions | null {
    try {
      const stored = localStorage.getItem('native_ciam_auth_options');
      if (!stored) {
        return null;
      }
      const parsed = JSON.parse(stored) as NativeAuthOptions;
      if (parsed.appId && parsed.ciamDomain && parsed.entraTenantID) {
        return { appId: parsed.appId, ciamDomain: parsed.ciamDomain, entraTenantID: parsed.entraTenantID };
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Retrieves the native bundle identifier at runtime via @capacitor/app.
   *
   * Android returns the applicationId (e.g. com.loandepot.portal.dev).
   * iOS returns the CFBundleIdentifier (e.g. com.loandepot.portal.dev).
   *
   * This ensures the redirect scheme always matches the native config
   * regardless of build flavor / scheme:
   *   Android redirectUrl: {bundleId}://oauth
   *   iOS redirectUrl:     msauth.{bundleId}://auth
   */
  private async getBundleId(): Promise<string> {
    const info = await App.getInfo();
    return info.id;
  }
}
